// Experiment lifecycle state machine, ported verbatim from thor-api-service:
//   - model/db/type/ExperimentState.java          (states + VALID_TRANSITIONS)
//   - model/db/type/ExperimentReviewType.java      (review types)
//   - service/state/handlers/*StateHandler.java     (per-transition requirements)
// The server is the source of truth; mirror these client-side for the
// state-transition UI (selectable targets, required sign-offs, validation gating).

// ── States (ExperimentState) ────────────────────────────────────────────────
export const EXPERIMENT_STATES = {
    design: {value: 'design', label: 'Design'},
    design_review: {value: 'design_review', label: 'Design Review'},
    instrumentation: {value: 'instrumentation', label: 'Instrumentation'},
    pre_prod_validation: {value: 'pre_prod_validation', label: 'Pre-prod Validation'},
    running: {value: 'running', label: 'Running'},
    paused: {value: 'paused', label: 'Paused'},
    cancelled: {value: 'cancelled', label: 'Cancelled'},
    ended: {value: 'ended', label: 'Test Ending and Decision'},
    cleanup: {value: 'cleanup', label: 'Clean Up'},
    finished: {value: 'finished', label: 'Finished'},
};

// Lifecycle order (for display / progress indicators).
export const EXPERIMENT_STATE_ORDER = [
    'design', 'design_review', 'instrumentation', 'pre_prod_validation',
    'running', 'paused', 'ended', 'cleanup', 'finished', 'cancelled',
];

// ── Valid transitions (ExperimentState.VALID_TRANSITIONS) ────────────────────
export const VALID_TRANSITIONS = {
    design: ['design_review', 'cancelled'],
    design_review: ['design', 'instrumentation'],
    instrumentation: ['design', 'pre_prod_validation'],
    pre_prod_validation: ['running'],
    running: ['ended', 'paused'],
    paused: ['running', 'ended'],
    cancelled: [],
    ended: ['cleanup'],
    cleanup: ['finished'],
    finished: ['paused'],
};

// ── Review types (ExperimentReviewType) ──────────────────────────────────────
export const REVIEW_TYPES = {
    analyst_design_review: {value: 'analyst_design_review', label: 'Analyst Design Review'},
    engineer_design_review: {value: 'engineer_design_review', label: 'Engineer Design Review'},
    engineer_instrumentation_signoff: {value: 'engineer_instrumentation_signoff', label: 'Engineer Instrumentation Signoff'},
    owner_pre_prod_signoff: {value: 'owner_pre_prod_signoff', label: 'Owner Pre-prod Signoff'},
    engineer_cleanup_signoff: {value: 'engineer_cleanup_signoff', label: 'Engineer Cleanup Signoff'},
};

// ── Per-transition requirements (service/state/handlers/*.validate) ───────────
// `skippable: true` means the server bypasses the check when the request sets
// `skipValidations`. `reviews` lists review types that must be approved first.
// Review/validation checks are also bypassed for A/A experiments (type === 'AA').
export const TRANSITION_REQUIREMENTS = {
    'design->design_review': {
        kind: 'validation', skippable: true,
        note: 'Experiment must pass validation (GET /{key}/validate). Skipped for A/A tests.',
    },
    'design_review->instrumentation': {
        kind: 'reviews', skippable: true,
        reviews: ['analyst_design_review', 'engineer_design_review'],
        note: 'Both design reviews must be approved. Skipped for A/A tests.',
    },
    'instrumentation->pre_prod_validation': {
        kind: 'reviews', skippable: true,
        reviews: ['engineer_instrumentation_signoff'],
        note: 'Engineer instrumentation signoff required.',
    },
    'pre_prod_validation->running': {
        kind: 'reviews', skippable: true,
        reviews: ['owner_pre_prod_signoff'],
        note: 'Owner pre-prod signoff required.',
    },
    'ended->cleanup': {
        kind: 'outcome', skippable: false,
        note: 'Experiment outcome/result must be provided before cleanup.',
    },
    'cleanup->finished': {
        kind: 'reviews', skippable: true,
        reviews: ['engineer_cleanup_signoff'],
        note: 'Engineer cleanup signoff required.',
    },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
export const getValidTransitions = (fromState) => VALID_TRANSITIONS[fromState] || [];

export const isValidTransition = (fromState, toState) =>
    getValidTransitions(fromState).includes(toState);

export const isTerminalState = (state) => getValidTransitions(state).length === 0;

export const getTransitionRequirement = (fromState, toState) =>
    TRANSITION_REQUIREMENTS[`${fromState}->${toState}`] || null;

export const getStateLabel = (state) =>
    EXPERIMENT_STATES[state]?.label || state;
